/* ----------------------------------------------------------------------
   CartDrawer - Shopify (Dawn-style)
   • Opens when
       1.  #cart-icon-bubble  (existing)
       2.  #cart-icon         (new)
       3.  Product added via AJAX
       4.  TAB key when drawer is closed
       5.  Cart quantity changes
   • Lightweight debug:  window.debugCartDrawer = false  to mute logs
------------------------------------------------------------------------ */

window.debugCartDrawer = true;            // flip to false in console to silence

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Track cart state
    this.previousCartCount = this.getCurrentCartCount();

    /* ————— Close drawer ————— */
    this.addEventListener('keyup', e => e.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')
        ?.addEventListener('click', () => this.close());

    /* ————— Original cart-icon-bubble trigger ————— */
    this.setHeaderCartIconAccessibility();

    /* (A)  #cart-icon click */
    document.querySelector('#cart-icon')?.addEventListener('click', e => {
      e.preventDefault();
      this.#log('#cart-icon click → open()');
      this.open(e.currentTarget);
    });

    /* (B)  Product added to cart */
    this.#patchShopifyOnItemAdded();
    document.addEventListener('item:added', () => {
      this.#log('item:added event → open()');
      this.checkCartChangesAndOpen();
    });

    /* (C)  TAB key */
    document.addEventListener('keydown', e => {
      if (e.code === 'Tab' && !this.classList.contains('active')) {
        this.#log('TAB pressed → open()');
        this.open();
      }
    });

    /* (D)  Cart update events */
    document.addEventListener('cart:updated', () => {
      this.#log('cart:updated event → open()');
      this.open();
    });

    // Listen for cart changes via PubSub
    if (window.PUB_SUB_EVENTS) {
      document.addEventListener(window.PUB_SUB_EVENTS.cartUpdate, () => {
        this.#log('PUB_SUB cartUpdate → open()');
        this.open();
      });
    }
  }

  /* ---------- Get current cart count ---------- */
  getCurrentCartCount() {
    const cartBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (cartBubble) {
      const count = parseInt(cartBubble.textContent) || 0;
      return count;
    }
    
    // Fallback: check cart items in drawer
    const cartItems = this.querySelectorAll('cart-drawer-items .cart-item');
    let totalCount = 0;
    cartItems.forEach(item => {
      const quantityInput = item.querySelector('.quantity__input');
      if (quantityInput) {
        totalCount += parseInt(quantityInput.value) || 0;
      }
    });
    
    return totalCount;
  }

  /* ---------- Check cart changes and open if needed ---------- */
  checkCartChangesAndOpen() {
    const currentCartCount = this.getCurrentCartCount();
    
    this.#log(`Cart count check: previous=${this.previousCartCount}, current=${currentCartCount}`);
    
    // Open drawer if cart count increased (new item added or quantity increased)
    if (currentCartCount > this.previousCartCount) {
      this.#log('Cart count increased → open()');
      this.open();
    }
    
    // Update tracked count
    this.previousCartCount = currentCartCount;
  }

  /* ---------- Accessibility for #cart-icon-bubble ---------- */
  setHeaderCartIconAccessibility() {
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return;
    bubble.setAttribute('role', 'button');
    bubble.setAttribute('aria-haspopup', 'dialog');

    bubble.addEventListener('click', e => {
      e.preventDefault();
      this.open(bubble);
    });
    bubble.addEventListener('keydown', e => {
      if (e.code.toUpperCase() === 'SPACE') {
        e.preventDefault();
        this.open(bubble);
      }
    });
  }

  /* ---------- Open drawer ---------- */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);

    setTimeout(() => {                    // ensures CSS transition fires
      this.classList.add('animate', 'active', 'nzd1');
      this.#log('drawer opened');
    });

    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusEl = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(container, focusEl);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  /* ---------- Close drawer ---------- */
 close() {
  this.classList.remove('active');

  setTimeout(() => {
    this.classList.remove('animate'); // Helps prevent flicker on next open
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.#log('drawer closed');
  }, 150); // Small delay to allow transition to complete
}
  /* ---------- Summary disclosure ---------- */
  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', 'false');
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);

    summary.addEventListener('click', e => {
      e.currentTarget.setAttribute('aria-expanded',
        !e.currentTarget.closest('details').hasAttribute('open'));
    });

    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* ---------- Render after fetch ---------- */
  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner?.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach(section => {
      const markup = parsedState.sections?.[section.id];
      if (!markup) {
        this.#log(`⚠️ missing markup for section ", ${section.id}`);
        return; // skip but continue processing others
      }

      const el = section.selector ? document.querySelector(section.selector)
                                  : document.getElementById(section.id);
      if (!el) {
        this.#log(`⚠️ element not found for section ", ${section.id}`);
        return;
      }

      const fragmentRoot = this.getSectionInnerHTML(markup, section.selector);
      if (fragmentRoot !== null) {
        el.innerHTML = fragmentRoot;
      }
    });

    // Update cart count after rendering
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')
          ?.addEventListener('click', this.close.bind(this));
      this.checkCartChangesAndOpen();
    });
  }

  /* ---------- Helpers ---------- */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const root = parsed.querySelector(selector);
    return root ? root.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer',        selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  setActiveElement(el) { this.activeElement = el; }

  #patchShopifyOnItemAdded() {
    if (!window.Shopify) return;

    const original = window.Shopify.onItemAdded;
    window.Shopify.onItemAdded = (...args) => {
      if (typeof original === 'function') original.apply(window.Shopify, args);
      this.#log('Shopify.onItemAdded → checking changes');
      this.checkCartChangesAndOpen();
    };
  }

  #log(...msg) {
    if (window.debugCartDrawer) console.debug('[CartDrawer]', ...msg);
  }
}

customElements.define('cart-drawer', CartDrawer);
window.CartDrawer = document.querySelector('cart-drawer');
/* ---------------- CartDrawerItems ---------------- */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer',       section: 'cart-drawer',      selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
